import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { Gutter, Headline } from '@gaiads/telia-react-component-library';
import { useDispatch, useSelector } from 'react-redux';
import { IndividualProductBundle, IndividualProductBundleTranslations } from '@cdaas/reusable-components-daas';
import { LocalBundleEnvelopeWithChosenMainVariant } from '@cdaas/reusable-components-daas/dist/src/product-api-types/api-types';
import { LocalBundle, LocalBundleEnvelope, SalesChannel } from '@cdaas/api-clients/dist/product-service-openapi/api';
import { OrganizationResponse, UserPoliciesResponseSingleDevicePolicyEnum, UserResponse } from '@cdaas/api-clients/dist/asset-admin-openapi';

import { productServiceSalesChannelItemsClient } from '../../../api-client/product-service-client';
import { AppDispatch } from '../../../store';
import { setOrderProduct, selectSalesChannel, selectOrganization, selectSelectedUser } from '../../../store/order';
import { ORDER_FLOW_CONSTANTS, setStorageItems } from '../../../utils';
import ConfirmModal from './ConfirmModal';

interface BundleState {
  bundle: LocalBundleEnvelope | null;
  loading: boolean;
  errorText: string;
}

export default function Bundle() {
  const salesChannel: SalesChannel | null = useSelector(selectSalesChannel);
  const organization: OrganizationResponse | null = useSelector(selectOrganization);
  const user: UserResponse | null = useSelector(selectSelectedUser);
  const [bundleState, setBundleState] = useState<BundleState>({
    bundle: null,
    loading: true,
    errorText: '',
  });
  const [chosenMainItemVariantId, setChosenMainItemVariantId] = useState<number>();
  const [confirmModalOpen, setConfirmModalOpen] = useState<boolean>(false);

  const { t } = useTranslation('orderFlow');
  const bundleId = useParams<{ bundleId: string }>().bundleId || '';
  const history = useHistory();
  const dispatch: AppDispatch = useDispatch();

  useEffect(() => {
    const getBundle = async (bundleId: string): Promise<void> => {
      try {
        const bundle = (
          await productServiceSalesChannelItemsClient.getSalesChannelBundle(salesChannel?.uuid || '', Number(bundleId), organization?.locale.language)
        ).data;
        setBundleState({
          errorText: bundle ? '' : t('bundle.getBundleErrorTitle'),
          loading: false,
          bundle,
        });
      } catch (error) {
        console.error(error);
        // TODO - handle error
        setBundleState({
          errorText: t('bundle.getBundleErrorTitle'),
          loading: false,
          bundle: null,
        });
      }
    };

    getBundle(bundleId);
  }, [bundleId, organization?.locale.language, salesChannel?.uuid, t]);

  const handleBuyButtonClick = (_bundle: LocalBundle, chosenMainItemVariantId: number) => {
    setChosenMainItemVariantId(chosenMainItemVariantId);
    setConfirmModalOpen(true);
  };

  const handleConfirm = () => {
    if (bundleState.bundle && chosenMainItemVariantId) {
      const selectedBundle: LocalBundleEnvelopeWithChosenMainVariant = {
        ...bundleState.bundle,
        chosenMainItemVariantId,
      };
      setStorageItems(['tItem'], [selectedBundle], 'session');
      dispatch(setOrderProduct(selectedBundle));
      history.push(`../${ORDER_FLOW_CONSTANTS.checkoutDetails}`);
    }
  };

  const handleCancel = () => {
    history.push(`../${ORDER_FLOW_CONSTANTS.selectProduct}`);
  };

  const bundleTranslations: IndividualProductBundleTranslations = {
    availabilityAvailable: t('bundle.availabilityAvailable'),
    availabilityFewLeft: t('bundle.availabilityFewLeft'),
    availabilityNotAvailable: t('bundle.availabilityNotAvailable'),
    availabilityReadyToShip: t('bundle.availabilityReadyToShip'),
    availabilityNotReadyToShip: t('bundle.availabilityNotReadyToShip'),
    showFullDescription: t('bundle.showFullDescription'),
    comesBundledUp: t('bundle.comesBundledUp'),
    paymentSingle: t('bundle.paymentSingle'),
    paymentMulti: t('bundle.paymentMulti'),
    totalPrice: t('bundle.totalPrice'),
    months: t('bundle.months'),
    totalMonthlyPrice: t('bundle.totalMonthlyPrice'),
    vat: t('bundle.vat'),
    includingVat: t('bundle.includingVat'),
    excludingVat: t('bundle.excludingVat'),
    readyButton: t('bundle.readyButton'),
    secondaryButton: t('bundle.secondaryButton'),
    perMonth: t('bundle.perMonth'),
    canNotShowBundle: t('bundle.canNotShowBundle'),
    currency: organization?.locale.currency || '',
    description: t('bundle.description'),
    dynamicPricingInsurance: {
      insured: t('bundle.dynamicPricingInsurance.insured'),
      shortExplanation: t('bundle.dynamicPricingInsurance.shortExplanation'),
      bulletPointFirstAccidents: t('bundle.dynamicPricingInsurance.bulletPointFirstAccidents'),
      bulletPointSecondTheft: t('bundle.dynamicPricingInsurance.bulletPointSecondTheft'),
      bulletPointThirdWater: t('bundle.dynamicPricingInsurance.bulletPointThirdWater'),
      terms: t('bundle.dynamicPricingInsurance.terms'),
      termsUrl: process.env.REACT_APP_INSURANCE_URI || '',
    },
    singleDevicePolicyTitle: t('bundle.singleDevicePolicyTitle'),
    singleDevicePolicyText: t('bundle.singleDevicePolicyText'),
    yourAllowance: t('bundle.yourAllowance'),
    yourAllowanceExplanation: t('bundle.yourAllowanceExplanation'),
    salaryDeductionEstimate: t('bundle.salaryDeductionEstimate'),
    addonProductImageAltText: t('bundle.addonProductImageAltText'),
    monthlySalaryDeductionExplanation: t('bundle.monthlySalaryDeductionExplanation'),
    oneTimeSalaryDeductionExplanation: t('bundle.oneTimeSalaryDeductionExplanation'),
  };

  return (
    <>
      <Gutter size={'layout-5'} />
      <Headline size={'lg'}>{t('bundle.title')}</Headline>
      <Gutter size={'layout-5'} />
      <IndividualProductBundle
        localBundleEnvelope={bundleState.bundle}
        errorText={bundleState.errorText}
        loading={bundleState.loading}
        translations={bundleTranslations}
        onReadyButtonClick={handleBuyButtonClick}
        onSecondaryButtonClick={handleCancel}
        singleDevicePolicyInEffect={user?.policies.single_device_policy === UserPoliciesResponseSingleDevicePolicyEnum.InEffect}
        allowance={user?.allowance}
        allowanceType={user?.allowance_type}
        locale={organization?.locale.language}
        procurementLimit={user?.policies.effective_lease_procurement_limit ? user.policies.effective_lease_procurement_limit : undefined}
      />
      <ConfirmModal
        isOpen={confirmModalOpen}
        user={user}
        organization={organization}
        chosenMainItemVariantId={chosenMainItemVariantId}
        bundleEnvelope={bundleState.bundle}
        handleCancel={handleCancel}
        handleConfirm={handleConfirm}
      />
    </>
  );
}
